import {Children, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import LinkButton from 'flarum/common/components/LinkButton';
import {ApiPayloadSingle} from 'flarum/common/Store';
import FieldList from '../fields/FieldList';
import ValueHistory from '../utils/ValueHistory';
import AbstractFormPage from './AbstractFormPage';
import SaveControls from '../components/SaveControls';
import FormTitle from '../components/FormTitle';
import PageContainer from '../components/PageContainer';
import Uploader from '../utils/Uploader';
import Submission from '../models/Submission';
import Form from '../models/Form';
import {formulaireFormShow, formulaireSubmission} from '../routes';
import isHorizontalLayout from '../utils/isHorizontalLayout';

const translationPrefix = 'kilowhat-formulaire.forum.submission.';

export default class FormShowPage extends AbstractFormPage {
    history!: ValueHistory
    uploader!: Uploader
    submission: Submission | null = null // Set once the form has been submitted

    oninit(vnode: Vnode) {
        super.oninit(vnode);

        this.bodyClass = 'FormulaireFormShowPage';
    }

    show(form: Form) {
        super.show(form);

        this.history = new ValueHistory({});
        this.uploader = new Uploader(form);
    }

    content(form: Form): Children {
        // TODO: show form for previews even if cannot submit
        if (!form.attribute('canSubmit')) {
            return m(PageContainer, {
                showSideNav: app.forum.attribute('formulaireShowSideNavOnSubmission'),
                narrow: true,
                noTheme: true,
            }, [
                m(FormTitle, {
                    form,
                }),
                m('p', app.translator.trans(translationPrefix + (form.attribute('isFull') ? 'form-full' : 'form-closed'))),
            ]);
        }

        if (this.submission) {
            const message = form.confirmationMessageHtml();
            return m(PageContainer, {
                showSideNav: app.forum.attribute('formulaireShowSideNavOnSubmission'),
                narrow: true,
                noTheme: true,
            }, [
                m(FormTitle, {
                    form,
                }),
                message ? m('div', m.trust(message)) : m('p', app.translator.trans(translationPrefix + 'submitted-successfully')),
                this.submission.attribute('canEdit') ? LinkButton.component({
                    className: 'Button',
                    href: formulaireSubmission(this.submission),
                }, app.translator.trans(translationPrefix + 'edit-submission')) : null,
                LinkButton.component({
                    className: 'Button',
                    href: formulaireFormShow(form),
                }, app.translator.trans(translationPrefix + 'submit-other')),
            ]);
        }

        const list = new FieldList({
            configs: form.template()!,
            submission: this.history.value,
            uploader: this.uploader,
            onchange: () => {
                this.history.save();
                this.dirty = true;
            },
            disabled: this.loading || this.uploader.uploading,
            validationErrors: this.validationErrors,
        });

        return m(PageContainer, {
            showSideNav: app.forum.attribute('formulaireShowSideNavOnSubmission'),
            narrow: true,
            noTheme: true,
        }, [
            m(FormTitle, {
                form,
            }),
            m('div', {
                className: isHorizontalLayout('StandaloneEdit') ? 'Formulaire-horizontal-layout' : '',
            }, list.listView()),
            m(SaveControls, {
                dirty: this.dirty,
                loading: this.loading,
                savedAt: this.savedAt,
                inError: Object.keys(this.validationErrors).length > 0,
                history: app.forum.attribute('formulaireShowHistoryControls') ? this.history : null,
                onsave: () => {
                    this.loading = true;

                    app.request<ApiPayloadSingle>({
                        method: 'POST',
                        url: app.forum.attribute('apiUrl') + form.apiEndpoint() + '/submissions',
                        body: {
                            data: {
                                attributes: {
                                    data: this.history.value,
                                },
                            },
                        },
                        errorHandler: this.onerror.bind(this),
                    }).then(data => {
                        this.submission = app.store.pushPayload<Submission>(data);
                        this.loading = false;
                        this.dirty = false;
                        this.savedAt = new Date();
                        this.validationErrors = {};
                        this.history.clear();
                        m.redraw();
                    }).catch(error => {
                        this.loading = false;
                        m.redraw();
                        throw error;
                    });
                },
            }),
        ]);
    }
}
